import { RefObject, useCallback, useMemo } from 'react'

import { AppId } from '@inno-tools/container-bridge'
import { ContextMenuRef } from '@inno-ui/context-menu'
import { TourStep } from '@inno-ui/onboarding-tour'
import { useAvailableApps } from 'modules/app-registry/hooks/useAvailableApps'
import { useTranslation } from 'react-i18next'
import { AppRefType } from 'shared/components/MainLayout/components/LaunchPad/LaunchPad.types'
import { AppGroup } from 'shared/constants/apps'
import { FEATURE_FLAGS } from 'shared/constants/featureFlags'
import { useLaunchpadStore } from 'shared/stores/launchpadStore'

export const LANDING_TOUR_ID = 'landing-tour'

export const USER_MENU_TOUR_ID = 'user-menu-tour'
export const CLUSTERIX_LOGO_TOUR_ID = 'clusterix-logo-tour'
export const TOP_DECK_TOUR_ID = 'top-deck-tour'
export const NOTIFICATION_CENTER_TOUR_ID = 'notification-center-tour'
export const UNIVERSAL_SEARCH_TOUR_ID = 'universal-search-tour'
export const APP_LIBRARY_TOUR_ID = 'app-library-tour'
export const ONBOARDING_PINNED_TOUR_ID = 'onboarding-pinned-app'
export const ONBOARDING_PINNED_APP_ID = AppId.INVENTORY

export const useOnboarding = ({
  launchpadIconRefs: _launchpadIconRefs,
  inventoryAppContextMenuRef
}: {
  inventoryAppContextMenuRef: RefObject<ContextMenuRef>
  launchpadIconRefs: AppRefType | undefined
}) => {
  const { t } = useTranslation('translation', { keyPrefix: 'onboarding' })
  const setIsLaunchpadOpen = useLaunchpadStore(
    state => state.setIsLaunchpadOpen
  )

  const { apps } = useAvailableApps()

  const tourIds = useMemo(() => {
    return Object.fromEntries(
      Object.values(apps).map(app => [app.id, `top-dock-${app.name}`])
    )
  }, [apps])

  const handleInventoryContextMenu = useCallback(async () => {
    try {
      const inventoryAppRef = inventoryAppContextMenuRef.current
      if (inventoryAppRef) {
        const menuElement =
          (await inventoryAppRef.openAndGetMenuElement()) as HTMLElement | null
        if (menuElement) {
          const firstChild = menuElement.querySelector(
            '[data-id="pin-to-dock"]'
          ) as HTMLElement | null
          if (firstChild) {
            // eslint-disable-next-line no-promise-executor-return
            await new Promise(resolve => setTimeout(resolve, 300))

            firstChild.style.backgroundColor = 'var(--primary-blue-dark)'
            firstChild.style.color = 'var(--white)'
            // eslint-disable-next-line no-promise-executor-return
            await new Promise(resolve => setTimeout(resolve, 800))

            const clickEvent = new MouseEvent('click', {
              bubbles: true,
              cancelable: true,
              view: window
            })
            firstChild.dispatchEvent(clickEvent)
          }
        }
      }
    } finally {
      setIsLaunchpadOpen(false)
    }
  }, [inventoryAppContextMenuRef, setIsLaunchpadOpen])

  const tourSteps: TourStep[] = useMemo(() => {
    return [
      {
        target: `[data-tour="${USER_MENU_TOUR_ID}"]`,
        title: t('user_menu.title'),
        content: t('user_menu.description'),
        highlightPadding: 0
      },
      {
        target: `[data-tour="${CLUSTERIX_LOGO_TOUR_ID}"]`,
        title: t('clusterix_logo.title'),
        content: t('clusterix_logo.description'),
        highlightPadding: 0
      },
      {
        target: `[data-tour="${TOP_DECK_TOUR_ID}"]`,
        title: t('top_dock.title'),
        content: t('top_dock.description'),
        highlightPadding: 0
      },
      {
        target: `[data-tour="${NOTIFICATION_CENTER_TOUR_ID}"]`,
        title: t('notification_center.title'),
        content: t('notification_center.description'),
        highlightPadding: 0
      },
      ...(FEATURE_FLAGS.SEARCH
        ? [
            {
              target: `[data-tour="${UNIVERSAL_SEARCH_TOUR_ID}"]`,
              title: t('universal_search.title'),
              content: t('universal_search.description'),
              highlightPadding: 0
            }
          ]
        : []),
      {
        target: `[data-tour="${APP_LIBRARY_TOUR_ID}"]`,
        title: t('app_library.title'),
        content: t('app_library.description'),
        highlightPadding: 0,
        pauseOnNext: true,
        onNext: () => {
          setIsLaunchpadOpen(true)
        }
      },
      {
        target: `[data-tour="${AppGroup.WORKFORCE_MANAGEMENT}"]`,
        title: t('category.title'),
        content: t('category.description'),
        highlightPadding: 0,
        pauseOnPrev: true,
        onPrev: () => {
          setIsLaunchpadOpen(false)
        }
      },
      {
        target: `[data-tour="${ONBOARDING_PINNED_TOUR_ID}"]`,
        title: t('pin_an_app.title'),
        content: t('pin_an_app.description'),
        highlightPadding: 0,
        pauseOnNext: true,
        onNext: handleInventoryContextMenu
      },
      {
        target: `[data-tour="${tourIds[AppId.INVENTORY]}"]`,
        title: t('pinned_top_dock.title'),
        content: t('pinned_top_dock.description'),
        highlightPadding: 0,
        pauseOnPrev: true,
        onPrev: () => {
          setIsLaunchpadOpen(true)
        }
      },
      {
        target: `[data-tour="${tourIds[AppId.SUBSIDIES]}"]`,
        title: t('refund_center_top_dock.title'),
        content: t('refund_center_top_dock.description'),
        highlightPadding: 0
      }
    ]
  }, [t, setIsLaunchpadOpen, handleInventoryContextMenu, tourIds])

  return {
    tourId: USER_MENU_TOUR_ID,
    tourSteps
  }
}
